import React, { useEffect, useId, useRef, useState } from 'react';
import { ArrowRight, Clock, Search, X } from 'lucide-react';
import { fetchSuggestions } from '../../services/api';
import { MIN_SEARCH_LENGTH } from '../../config';
import { useRecentSearches } from '../../hooks/useRecentSearches';
import { cx } from '../../lib/cx';
import { Kbd } from '../ui/misc';

const isTypingTarget = (el) =>
  el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);

/**
 * Search input with typeahead (ARIA combobox). Shows recent searches when
 * empty, server suggestions while typing, and validates the minimum length.
 * Press "/" or Ctrl/⌘+K anywhere to focus it.
 */
export const SearchBox = ({ initialValue = '', onSearch, disabledReason = '', className }) => {
  const [value, setValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [error, setError] = useState('');
  const recent = useRecentSearches();
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const listId = useId();
  const errorId = useId();

  // Keep the box in sync when the URL query changes (Back/Forward, links).
  useEffect(() => { setValue(initialValue); }, [initialValue]);

  const trimmed = value.trim();
  const showingRecent = trimmed.length === 0;
  const options = showingRecent ? recent : suggestions;

  // Debounced suggestions; stale responses are ignored.
  useEffect(() => {
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      fetchSuggestions(trimmed, controller.signal)
        .then((list) => setSuggestions(list.filter((s) => s.toLowerCase() !== trimmed.toLowerCase())))
        .catch(() => {});
    }, 200);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed]);

  useEffect(() => { setActive(-1); }, [options.length, showingRecent]);

  useEffect(() => {
    const onKey = (e) => {
      const slash = e.key === '/' && !isTypingTarget(document.activeElement);
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (slash || cmdK) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    const onPointer = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, []);

  const submit = (term) => {
    const q = (term ?? value).trim();
    if (q.length < MIN_SEARCH_LENGTH) {
      setError(`Type at least ${MIN_SEARCH_LENGTH} characters to search.`);
      inputRef.current?.focus();
      return;
    }
    if (disabledReason) {
      setError(disabledReason);
      return;
    }
    setError('');
    setValue(q);
    setOpen(false);
    onSearch(q);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActive((i) => (options.length ? (i + 1) % options.length : -1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (options.length ? (i <= 0 ? options.length - 1 : i - 1) : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && active >= 0 && options[active]) submit(options[active]);
      else submit();
    } else if (e.key === 'Escape') {
      if (open && options.length) {
        e.preventDefault();
        setOpen(false);
      } else if (value) {
        e.preventDefault();
        setValue('');
      }
    }
  };

  const listVisible = open && options.length > 0;
  const optionId = (i) => `${listId}-opt-${i}`;

  return (
    <div ref={wrapperRef} className={cx('relative', className)}>
      <form
        role="search"
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className={cx(
          'flex h-10 items-center rounded-xl border bg-sunken transition-colors focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25',
          error ? 'border-danger/60' : 'border-line-strong',
        )}
      >
        <Search size={16} className="ml-3 shrink-0 text-faint" aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          value={value}
          onChange={(e) => { setValue(e.target.value); setOpen(true); if (error) setError(''); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search tests across all labs…"
          aria-label="Search tests"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={listVisible}
          aria-controls={listId}
          aria-activedescendant={listVisible && active >= 0 ? optionId(active) : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="search"
          className="h-full min-w-0 flex-1 bg-transparent px-2.5 text-sm text-fg placeholder:text-faint focus:outline-none focus-visible:outline-none [&::-webkit-search-cancel-button]:hidden"
        />
        {value && (
          <button
            type="button"
            onClick={() => { setValue(''); setError(''); inputRef.current?.focus(); }}
            className="mr-1 rounded-md p-1.5 text-faint hover:text-fg"
            aria-label="Clear search"
          >
            <X size={14} aria-hidden="true" />
          </button>
        )}
        {!value && (
          <span className="mr-2 hidden sm:inline-flex" aria-hidden="true"><Kbd>/</Kbd></span>
        )}
        <button
          type="submit"
          className="mr-1 inline-flex h-8 items-center gap-1.5 rounded-lg bg-accent-solid px-3 text-sm font-medium text-on-accent transition-colors hover:bg-accent-solid/90"
        >
          <span className="hidden sm:inline">Search</span>
          <ArrowRight size={15} aria-hidden="true" className="sm:hidden" />
          <span className="sr-only sm:hidden">Search</span>
        </button>
      </form>

      {error && (
        <p id={errorId} role="alert" className="absolute left-1 top-full mt-1 text-xs text-danger">{error}</p>
      )}

      <ul
        id={listId}
        role="listbox"
        aria-label={showingRecent ? 'Recent searches' : 'Suggestions'}
        className={cx(
          'absolute inset-x-0 top-full z-40 mt-1.5 max-h-80 overflow-y-auto rounded-xl border border-line bg-surface p-1 shadow-overlay scrollbar-thin',
          !listVisible && 'hidden',
        )}
      >
        {showingRecent && listVisible && (
          <li role="presentation" className="px-3 pb-1 pt-1.5 text-xs font-medium text-faint">Recent searches</li>
        )}
        {options.map((option, i) => (
          <li
            key={option}
            id={optionId(i)}
            role="option"
            aria-selected={i === active}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => submit(option)}
            onMouseEnter={() => setActive(i)}
            className={cx(
              'flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-sm',
              i === active ? 'bg-raised text-fg' : 'text-fg-2',
            )}
          >
            {showingRecent
              ? <Clock size={14} className="shrink-0 text-faint" aria-hidden="true" />
              : <Search size={14} className="shrink-0 text-faint" aria-hidden="true" />}
            <span className="truncate">{option}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
